import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Box, Collapse, useTheme} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import AppDialog from 'src/components-new/feedback/AppDialog'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import SelectNew from 'src/components-new/inputs/SelectNew'
import Button from 'src/components-new/primitives/Button'
import {STATUS, statesOptions} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import type {FilterResult} from './Filters'

interface DoctorsLookupDialogProps {
  onClose: () => void
  onRowClick: (row: Record<string, any>) => void
  customFilters?: Record<string, string | string[]>[]
}

const DEFAULT_PAGINATION: PaginationState = {page: 0, rowsPerPage: 10}

const COLUMNS: ColumnDef<Record<string, any>>[] = [
  {key: 'lastName', header: 'COGNOME', render: (row) => <>{row.lastName ?? '—'}</>},
  {key: 'firstName', header: 'NOME', render: (row) => <>{row.firstName ?? '—'}</>},
  {key: 'email', header: 'EMAIL', render: (row) => <>{row.email ?? '—'}</>},
  {key: 'state', header: 'STATO', render: (row) => <>{row.state ?? '—'}</>},
]

/**
 * New-style, responsive equivalent of the legacy
 * src/components/templates/DoctorsLookupDialog/DoctorsLookupDialog.tsx, used by
 * components-new/data/Filters.tsx's `doctorLookup` field type (mirrors how
 * PatientLookupDialog replaced the legacy patient lookup dialog).
 *
 * The legacy dialog rendered its Cognome/Nome/Stato filters in an antd Popover
 * positioned with `getPopupContainer={(triggerNode) => triggerNode}` and listed
 * results in the legacy, non-responsive TableCustom. Neither adapts to a narrow
 * viewport: the Popover has no `max-width`, so under `position: absolute` it sizes
 * to its own 3-column grid's shrink-to-fit width and simply extends past the
 * viewport edge instead of wrapping, and TableCustom has no mobile card layout —
 * both got visibly clipped when this dialog opened inside "Centri medici"' Filtra
 * drawer on mobile. Building on AppDialog + DataTable (like PatientLookupDialog)
 * puts the filters in normal document flow via Collapse and gives the results
 * table DataTable's existing responsive desktop-table/mobile-card behavior.
 */
export default function DoctorsLookupDialog({onClose, onRowClick, customFilters = []}: DoctorsLookupDialogProps): JSX.Element {
  const theme = useTheme()
  // Opened from inside components-new/data/Filters.tsx's Drawer (FILTERS_DRAWER_Z_INDEX
  // = modal + 1) — without this it renders behind that Drawer instead of above it.
  // Matches FILTERS_POPUP_Z_INDEX there exactly.
  const dialogZIndex = theme.zIndex.modal + 2
  // A Select's menu opened from inside this dialog needs to clear the dialog's own
  // z-index, or it renders behind this dialog's Paper — present in the DOM but inert.
  const fieldPopupZIndex = theme.zIndex.modal + 3

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [lastName, setLastName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [state, setState] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<FilterResult[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [count, setCount] = useState(0)
  const [status, setStatus] = useState<string>()
  const [pagination, setPagination] = useState<PaginationState>(DEFAULT_PAGINATION)
  // Guards against out-of-order responses: the initial, unfiltered fetch this dialog fires on
  // mount can still be in flight when the user opens "Filtra" and searches — without this, a
  // slower unfiltered response landing after a faster filtered one silently overwrites the
  // filtered result with the full list, making the search appear to do nothing.
  const latestRequestIdRef = useRef(0)

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current
    const filters = [...appliedFilters, ...customFilters]
    const paginationFilters = {rowsPerPage: pagination.rowsPerPage, page: pagination.page, order: []}
    const fetchedData = await apiClient.getDoctorsSearch(filters, setStatus, paginationFilters) ?? []
    const fetchedCount = await apiClient.getDoctorsCount(filters, setStatus) ?? 0
    if (requestId !== latestRequestIdRef.current) return
    setData(fetchedData.map((doctor: any) => ({
      ...doctor,
      state: statesOptions.find((option) => option.value === doctor.state)?.label ?? doctor.state,
    })))
    const n = Number(fetchedCount)
    if (!isNaN(n)) setCount(n)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, pagination, customFilters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSearch = () => {
    setAppliedFilters([
      ...(lastName ? [{property: 'lastName', type: 'text', value: lastName}] : []),
      ...(firstName ? [{property: 'firstName', type: 'text', value: firstName}] : []),
      ...(state ? [{property: 'state', type: 'select', value: state}] : []),
    ])
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleClear = () => {
    setLastName('')
    setFirstName('')
    setState('')
    setAppliedFilters([])
    setPagination(prev => ({...prev, page: 0}))
  }

  return (
    <AppDialog
      actions={
        <>
          <Button onClick={handleClear} variant="secondary">Annulla</Button>
          <Button onClick={handleSearch} variant="primary">Cerca</Button>
        </>
      }
      maxWidth="lg"
      onClose={onClose}
      sx={{zIndex: dialogZIndex}}
      open
      title="Dottori"
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <Button
          onClick={() => setFiltersOpen(prev => !prev)}
          startIcon={<FilterListIcon />}
          sx={{alignSelf: 'flex-start'}}
          variant="secondary"
        >
          {filtersOpen ? 'Nascondi filtri' : 'Filtra'}
        </Button>

        <Collapse in={filtersOpen}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
              gap: 2,
              pb: 1,
            }}
          >
            <TextFieldNew fullWidth label="Cognome" onChange={setLastName} value={lastName} />
            <TextFieldNew fullWidth label="Nome" onChange={setFirstName} value={firstName} />
            <SelectNew
              allowClear
              fullWidth
              label="Stato"
              menuZIndex={fieldPopupZIndex}
              onChange={setState}
              options={statesOptions}
              value={state}
            />
          </Box>
        </Collapse>

        <DataTable<Record<string, any>>
          columns={COLUMNS}
          emptyMessage="Nessun dottore trovato"
          keyField="_id"
          loading={status === STATUS.PENDING}
          onPaginationChange={setPagination}
          onRowClick={onRowClick}
          pagination={pagination}
          rows={data}
          totalElements={count}
        />
      </Box>
    </AppDialog>
  )
}
